import React from "react";

import { Formik } from "formik";
import { Box } from "@mui/material";
import * as Yup from "yup";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import ModernModal from "../../../common/ModernModal";
import {
  ModernFormField,
  ModernSubmitButton,
  ModernFormAlert,
  ModernRadioField,
} from "../../../common/ModernForm";
import { AddUserToOrganizationPayload } from "../../services/user-management.service";
import { RoleName } from "../../../common/enums/RoleName";
import { userManagementService } from "../../services";

interface Props {
  handleClose: () => void;
  open: boolean;
}

const roleDescriptions: Record<RoleName, string> = {
  [RoleName.VIEWER]: "Can view content and access basic features",
  [RoleName.EDITOR]: "Can create and modify content",
  [RoleName.ADMIN]: "Full access to all features and user management",
};

const roleOptions = Object.values(RoleName).map((role) => ({
  value: role,
  label: role,
  description: roleDescriptions[role],
}));

export default function AddUserDialog({ handleClose, open }: Props) {
  const queryClient = useQueryClient();

  const { mutateAsync, isError, error, isPending, isSuccess } = useMutation({
    mutationKey: ["add-user-to-organization"],
    mutationFn: (values: AddUserToOrganizationPayload) =>
      userManagementService.adduserToOrganization(values),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["User Management"] });
      setTimeout(handleClose, 1200);
    },
  });

  const handleAddUserToOrganization = async (values: {
    email: string;
    role: RoleName;
  }) => {
    await mutateAsync({
      email: values.email,
      roles: [values.role],
    });
  };

  return (
    <ModernModal
      open={open}
      onClose={handleClose}
      title="Add User to Organization"
      maxWidth="sm"
    >
      <Formik
        initialValues={{ email: "", role: RoleName.VIEWER }}
        validationSchema={Yup.object({
          email: Yup.string()
            .email("Please enter a valid email address")
            .required("Email is required"),
          role: Yup.string().required("Role is required"),
        })}
        onSubmit={handleAddUserToOrganization}
      >
        {(formik) => (
          <form onSubmit={formik.handleSubmit}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {isError && (
                <ModernFormAlert severity="error">
                  {error?.message || "Failed to add user. Please try again."}
                </ModernFormAlert>
              )}

              {isSuccess && (
                <ModernFormAlert severity="success">
                  User Successfully Added!
                </ModernFormAlert>
              )}

              <ModernFormField
                label="Email Address"
                name="email"
                type="email"
                error={!!formik.errors.email && formik.touched.email}
                helperText={formik.touched.email ? formik.errors.email : ""}
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder="Enter user's email address"
                required
              />

              <ModernRadioField
                label="User Role"
                name="role"
                value={formik.values.role}
                onChange={formik.handleChange}
                options={roleOptions}
                error={!!formik.errors.role && formik.touched.role}
                helperText={formik.touched.role ? formik.errors.role : ""}
              />

              <ModernSubmitButton
                label="Add User"
                loading={isPending}
                disabled={isPending || !formik.isValid || !formik.dirty}
                fullWidth
              />
            </Box>
          </form>
        )}
      </Formik>
    </ModernModal>
  );
}
